import { useCallback, useEffect, useRef, useState } from 'react';

import { Schemas } from '@api-client/generated/types';
import { useNewsletterController_getRecipients } from '@api-client/generated/NewsletterController/getRecipients';
import { useNewsletterController_sync } from '@api-client/generated/NewsletterController/sync';
import { useNewsletterController_syncStatus } from '@api-client/generated/NewsletterController/syncStatus';

export type RecipientCompany = Schemas.RecipientCompanyDto;
export type SyncSummary = Schemas.SyncSummaryDto;

/**
 * Which pool recipients are drawn from (backend `audience` selector, ENG-1198):
 * `in_service` is every member of an in-service company, `opted_in` narrows that
 * to members who opted in to marketing. A confirmed email is required either way.
 *
 * The enum reached the deployed stage schema with easybiz-backend #227, so the
 * type is the generated one now; the list stays a local runtime value, checked
 * against that type so it cannot drift from the schema unnoticed.
 */
export type NewsletterAudience = Schemas.NewsletterAudience;
export const NEWSLETTER_AUDIENCES = [
  'in_service',
  'opted_in',
] as const satisfies readonly NewsletterAudience[];
export const DEFAULT_AUDIENCE: NewsletterAudience = 'in_service';

export interface SyncExclusions {
  excludedCompanyIds: string[];
  excludedUserIds: string[];
}

/** The sync body: exclusions plus the audience they were derived from. */
type SyncRequestBody = Schemas.SyncRequestDto & { audience: NewsletterAudience };

/** pgBoss states that mean the job is done; anything else keeps polling. */
const TERMINAL_STATES = new Set(['completed', 'failed', 'cancelled', 'expired']);
const POLL_INTERVAL_MS = 1500;
/**
 * Hard ceiling on how long we wait for the job to confirm. Past this we stop
 * polling and tell the user it's taking too long (it may still finish in
 * Listmonk) rather than pinning the spinner indefinitely. Also drives the
 * countdown shown on the Sync button.
 */
export const SYNC_TIMEOUT_MS = 30_000;
const SYNC_TIMEOUT_SECONDS = Math.round(SYNC_TIMEOUT_MS / 1000);

/**
 * Newsletter data layer for the backoffice page: the recipient tree
 * (`GET /admin/newsletter/recipients`), the full-rewrite sync mutation
 * (`POST …/sync`), and the job-status poll (`GET …/sync/:jobId`). The controller
 * takes exclusions, so the page derives `{ excludedCompanyIds, excludedUserIds }`
 * and hands them straight through. Polling runs only while a job id is set, the
 * job has not reached a terminal state, and the 30s ceiling has not elapsed.
 *
 * `audience` selects the recipient pool and is sent on both calls, so the sync
 * re-derives the very set the tree was showing.
 */
export const useNewsletter = (audience: NewsletterAudience = DEFAULT_AUDIENCE) => {
  // The audience is a declared query parameter, so it travels as `params` — which
  // is also what the wrapper builds the cache key from, so switching refetches
  // rather than reusing the other audience's tree.
  const recipientsQuery = useNewsletterController_getRecipients({
    params: { audience },
  });
  const syncMutation = useNewsletterController_sync();
  const [jobId, setJobId] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  // Whole seconds left before the ceiling; null when no job is being awaited.
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const startedAtRef = useRef<number | null>(null);

  const statusQuery = useNewsletterController_syncStatus({
    params: { jobId: jobId ?? '' },
    config: {
      // Stop hitting the endpoint once we've given up waiting.
      enabled: jobId !== null && !timedOut,
      refetchInterval: (query) => {
        const state = query.state.data?.state;
        return state && TERMINAL_STATES.has(state) ? false : POLL_INTERVAL_MS;
      },
    },
  });

  const jobState = statusQuery.data?.state;
  // A persistently failing status poll must not pin the spinner forever: treat a
  // query error as a terminal (failed) outcome so the UI resolves and reports it.
  const pollErrored = jobId !== null && statusQuery.isError;
  const jobReachedTerminal = jobState !== undefined && TERMINAL_STATES.has(jobState);
  const isJobRunning =
    jobId !== null && !timedOut && !pollErrored && !jobReachedTerminal;

  // While the job is running, tick down the countdown and enforce the ceiling.
  useEffect(() => {
    if (!isJobRunning) {
      setSecondsLeft(null);
      return;
    }
    const tick = () => {
      const startedAt = startedAtRef.current;
      if (startedAt === null) return;
      const remaining = Math.max(
        0,
        Math.ceil((SYNC_TIMEOUT_MS - (Date.now() - startedAt)) / 1000)
      );
      setSecondsLeft(remaining);
      if (remaining === 0) setTimedOut(true);
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [isJobRunning]);

  const sync = useCallback(
    async (exclusions: SyncExclusions) => {
      setTimedOut(false);
      // The audience travels with the exclusions: the backend re-derives the
      // recipient set, so without it the sync would rewrite the list from the
      // default audience regardless of what the marketer was looking at.
      const requestBody: SyncRequestBody = { ...exclusions, audience };
      const accepted = await syncMutation.mutateAsync({ requestBody });
      startedAtRef.current = Date.now();
      setSecondsLeft(SYNC_TIMEOUT_SECONDS);
      setJobId(accepted.jobId);
      return accepted;
    },
    [syncMutation, audience]
  );

  const dismissResult = useCallback(() => {
    setJobId(null);
    setTimedOut(false);
    setSecondsLeft(null);
    startedAtRef.current = null;
  }, []);

  const jobFailed = jobState !== undefined && jobState !== 'completed' && TERMINAL_STATES.has(jobState);

  return {
    companies: recipientsQuery.data ?? [],
    isLoading: recipientsQuery.isLoading,
    isError: recipientsQuery.isError,
    refetch: recipientsQuery.refetch,

    sync,
    // Cover both phases: the enqueue request, then the job running to completion.
    isSyncing: syncMutation.isPending || isJobRunning,
    // Seconds left on the 30s ceiling while the job runs; null otherwise.
    syncCountdown: secondsLeft,
    // Surface whichever failed — the enqueue call or the status poll.
    syncError: syncMutation.error ?? statusQuery.error,
    // Populated once the job finishes successfully; null while pending or on failure.
    syncSummary: jobState === 'completed' ? statusQuery.data?.summary ?? null : null,
    syncFailed: jobFailed || pollErrored,
    // We gave up waiting: the job may still complete in Listmonk.
    syncTimedOut: timedOut,
    dismissResult,
  };
};
